import React, { useEffect, useMemo, useState } from "react";
import "./ProfilePage.css"; // rp-* tasarımını reuse ediyoruz

function VerifiedTick(props) {
  return (
    <svg viewBox="0 0 100 100" fill="none" {...props}>
      <path
        d="M 25 55 Q 30 40 35 55 L 45 75 L 75 35 Q 85 30 85 45"
        stroke="#10b981"
        strokeWidth="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconBack(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
      <path
        d="M15 18l-6-6 6-6"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function initialsFromName(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "PP";
  const a = parts[0][0] || "";
  const b = parts.length > 1 ? (parts[1][0] || "") : (parts[0][1] || "");
  return (a + b).toUpperCase() || "PP";
}

function getHashParams() {
  const hash = window.location.hash || "";
  const qIndex = hash.indexOf("?");
  const qs = qIndex === -1 ? "" : hash.substring(qIndex + 1);
  return new URLSearchParams(qs);
}

function getIdFromHashQuery() {
  const params = getHashParams();
  const id = params.get("id");
  return id ? Number(id) : null;
}

function getSourceFromHashQuery() {
  const params = getHashParams();
  return params.get("source") || "";
}

function getSearchQFromHashQuery() {
  const params = getHashParams();
  return params.get("q") || "";
}

export default function UserProfilePage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [profile, setProfile] = useState(null);

  const [postCountTotal, setPostCountTotal] = useState(0);
  const [followerCount, setFollowerCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [posts, setPosts] = useState([]);

  const [avatarFailed, setAvatarFailed] = useState(false);

  const userId = useMemo(() => getIdFromHashQuery(), []);
  const source = useMemo(() => getSourceFromHashQuery(), []);
  const searchQ = useMemo(() => getSearchQFromHashQuery(), []);

  function goBackSmart() {
    if (source === "profile-requests") {
      window.location.href = "/react#/profile";
      return;
    }

    if (source === "home-search") {
      const qPart = searchQ ? `&q=${encodeURIComponent(searchQ)}` : "";
      window.location.href = `/react#/dashboard?panel=search${qPart}`;
      return;
    }

    window.location.href = "/react#/dashboard";
  }

  useEffect(() => {
    setAvatarFailed(false);
  }, [userId]);

  useEffect(() => {
    if (!userId || Number.isNaN(userId)) {
      setError("Missing user id");
      setLoading(false);
      return;
    }

    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setError("");

        const res = await fetch(
          `/api/public-profile?id=${encodeURIComponent(String(userId))}`,
          { credentials: "include" }
        );

        const data = await res.json().catch(() => null);
        if (!alive) return;

        if (res.ok && data?.ok && data.self) {
          if (source === "profile-requests") {
            window.location.href = "/react#/profile";
          } else if (source === "home-search") {
            const qPart = searchQ ? `&q=${encodeURIComponent(searchQ)}` : "";
            window.location.href = `/react#/dashboard?panel=search${qPart}`;
          } else {
            window.location.href = "/react#/profile";
          }
          return;
        }

        if (!res.ok || !data?.ok) {
          setError((data && data.error) || `HTTP ${res.status}`);
          setLoading(false);
          return;
        }

        setProfile(data.profile || null);
        setPostCountTotal(Number(data.postCountTotal) || 0);
        setFollowerCount(Number(data.followerCount) || 0);
        setFollowingCount(Number(data.followingCount) || 0);
        setPosts(Array.isArray(data.posts) ? data.posts : []);
        setAvatarFailed(false);
        setLoading(false);
      } catch (e) {
        if (!alive) return;
        setError("Network error");
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [userId, source, searchQ]);

  if (loading) {
    return (
      <div className="rp-root">
        <div className="rp-shell">
          <div className="rp-loading">Loading profile...</div>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="rp-root">
        <div className="rp-shell">
          <div className="rp-error">Error: {error || "Unknown error"}</div>
        </div>
      </div>
    );
  }

  const nameText = profile.fullName || "User";
  const uniText = profile.university || "—";
  const relText =
    profile.relationshipStatus && String(profile.relationshipStatus).trim()
      ? profile.relationshipStatus
      : "Not set";
  const bioText = profile.bio && String(profile.bio).trim() ? profile.bio : "";

  const avatarSrc = `/post-author-avatar?userId=${encodeURIComponent(
    String(profile.id)
  )}`;

  const grid = [posts[0] || null, posts[1] || null, posts[2] || null];

  return (
    <div className="rp-root">
      <div className="rp-shell">
        <div className="rp-header">
          <div className="rp-header-left">
            <h1 className="rp-h1">{nameText}</h1>
            <div className="rp-tick-wrap">
              <VerifiedTick className="rp-tick" />
            </div>
          </div>

          <button
            className="rp-settings"
            type="button"
            title="Back"
            onClick={goBackSmart}
          >
            <IconBack className="rp-settings-icon" />
          </button>
        </div>

        <div className="rp-main">
          <div className="rp-toprow">
            <div className="rp-avatar-frame">
              <div className="rp-avatar-outer" />
              <div className="rp-avatar-inner">
                {!avatarFailed ? (
                  <img
                    className="rp-avatar-img"
                    src={avatarSrc}
                    alt={nameText}
                    onError={() => setAvatarFailed(true)}
                  />
                ) : (
                  <div className="rp-avatar-fallback">
                    {initialsFromName(nameText)}
                  </div>
                )}
              </div>
            </div>

            <div className="rp-stats-wrap">
              <div className="rp-stats">
                <div className="rp-stat">
                  <span className="rp-stat-label">posts</span>
                  <span className="rp-stat-value">{postCountTotal}</span>
                </div>
                <div className="rp-stat">
                  <span className="rp-stat-label">followers</span>
                  <span className="rp-stat-value">{followerCount}</span>
                </div>
                <div className="rp-stat">
                  <span className="rp-stat-label">following</span>
                  <span className="rp-stat-value">{followingCount}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="rp-bottomrow">
            <div className="rp-info">
              <div className="rp-info-block">
                <span className="rp-info-label">University:</span>
                <span className="rp-info-value">{uniText}</span>
              </div>

              <div className="rp-info-block">
                <span className="rp-info-label">Relationship Status:</span>
                <span className="rp-info-value">{relText}</span>
              </div>
            </div>

            <div className="rp-bio-wrap">
              <div className="rp-bio-box">
                <div className="rp-bio-input" style={{ fontStyle: "italic" }}>
                  {bioText || " "}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="rp-divider" />

        <div className="rp-grid">
          {grid.map((p, i) => {
            if (!p) {
              return (
                <div key={`ph-${i}`} className="rp-tile">
                  <div className="rp-tile-bubble">
                    <span className="rp-tile-num">{i + 1}</span>
                  </div>
                </div>
              );
            }

            const href = `/react#/dashboard?scrollTo=post-${p.id}`;

            if (p.hasImage) {
              return (
                <a key={p.id} href={href} className="rp-tile rp-tile-link" title="View post">
                  <img className="rp-tile-img" src={`/post-image?id=${p.id}`} alt="Post" loading="lazy" />
                </a>
              );
            }

            return (
              <a key={p.id} href={href} className="rp-tile rp-tile-link" title="View post">
                <div className="rp-tile-text">{p.previewText || ""}</div>
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
}